import React, {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Map as LeafletMap } from "leaflet";
import Control from "react-leaflet-custom-control";
import { assign, clone, isNumber } from "lodash";

import { ExecutorLocations, Map as MapService } from "../../../services";
import { ExecutorStatus } from "../../../services/Executor";
import {
	store,
	useTypedDispatch,
	useTypedSelector,
} from "../../../redux/store";
import { Orders } from "../../../redux/reducers/Orders";
import orderPage from "../../../redux/reducers/OrdersPage";
import {
	useTaxiServiceMapName,
	useTaxiServiceIdsDecoder,
	useCurrentTaxiService,
	useObjectEditor,
	useMapSearch,
	useTaxiServiceFilterAccessControl,
	useCarParkFilterAccessControl,
	useCarParkIdsDecoder,
	selectFilteredExecutorLocations,
} from "../../../hooks";
import MapBase, { useChanged } from "../../DispatcherMap";
import { addressToString } from "../../../utils";
import MapExecutorLocations from "../../MapExecutorLocations";
import { AccessGuard } from "../../common";

import useMapActions from "./hooks/useMapActions";
import { TabAccessNames, TabKeys } from "./constants/access";
import {
	Header,
	Root,
	ControlButtons,
	ShowButtons,
	PriceZones,
	Sectors,
	Parkings,
	Route,
	MapObject,
} from "./components";

import type {
	ExecutorLocationFilterAccess,
	ExecutorLocationSubscribeOptions,
} from "../../../hooks/subscriptionHandlers/useExecutorLocationsSubHandler";

function isMapObject(
	object: MapService.Search.Object | ExecutorLocations.Model,
): object is MapService.Search.Object {
	return "geometry" in object;
}

const headerValueStatusToExecutorStatus = {
	ownOrder: null,
	free: ExecutorStatus.AVAILABLE,
	busy: ExecutorStatus.BUSY,
	lunch: ExecutorStatus.DINNER,
	home: ExecutorStatus.HOME,
	onOrder: ExecutorStatus.ON_ORDER,
	closedByOffice: ExecutorStatus.CLOSED,
};

const Map: React.FC<Map.Props> = () => {
	const {
		setMapWidgetOrderTabExecutorFilters,
		setMapWidgetCenter,
		setMapWidgetZoom,
	} = useMapActions();
	const decodeTaxiServiceIds = useTaxiServiceIdsDecoder();
	const { accessTaxiServicesIds, accessCompaniesIds } =
		useTaxiServiceFilterAccessControl<any>();
	const { isUserFleetOwner, accessFleetsIdsOnlyActive } =
		useCarParkFilterAccessControl<any>();
	const allCarParkIds = useCarParkIdsDecoder();
	const fleetIdsAll = useMemo(() => allCarParkIds(["all"]), [allCarParkIds]);
	const locationAccess = useMemo<ExecutorLocationFilterAccess>(
		() => ({
			isUserFleetOwner,
			accessFleetsIdsOnlyActive,
			fleetIdsAll,
		}),
		[accessFleetsIdsOnlyActive, fleetIdsAll, isUserFleetOwner],
	);

	const mapRef = useRef<LeafletMap | null>(null);
	const mapObjectRef = useRef<MapObject.Controller | null>(null);
	const routeRef = useRef<Route.Controller | null>(null);
	const executorsRef = useRef<MapExecutorLocations.Controller | null>(null);

	const taxiServiceName = useTaxiServiceMapName();
	const currentTaxiService = useCurrentTaxiService();

	const mapCenter = currentTaxiService.coordinates;

	const dispatch = useTypedDispatch();
	const language = useTypedSelector((state) => state.session.language);
	const { searchRadiusMeters: searchRadius } = useTypedSelector(
		(state) => state.settings.map,
	);
	const activeOrderTab = useTypedSelector(
		(state) => state.ordersPageReducer.ordersType,
	);
	const mapWidgetDefaultExecutorsFilters = useTypedSelector(
		(state) => state.orders.map.filter.persistent.executors.defaultFilters,
	);
	const mapWidgetExecutorFilters = useTypedSelector(
		(state) =>
			state.orders.map.filter.persistent.executors.filtersByOrderTab[
				activeOrderTab
			],
	);

	const btnOnMap = useTypedSelector(
		(state) => state.orders.map.toggles.btnOnMap,
	);
	const activeOrder = useTypedSelector(
		(state) => state.ordersPageReducer.activeOrder,
	);
	const highlightedExecutorId = useMemo(() => {
		if (!activeOrder || !("id" in activeOrder)) return undefined;
		return (
			activeOrder.executorToOrder?.[0]?.executor?.id ??
			activeOrder.offer?.executor?.id
		);
	}, [activeOrder]);
	const activePoint = useTypedSelector(
		(state) => state.ordersPageReducer.activePoint,
	);

	const defaultMapCenter = useMemo(
		() => ({ lat: 50.455002, lng: 30.511284 }),
		[],
	);

	const [object, setObject] = useState<MapService.Search.Object | null>(null);
	const [searchValue, setSearchValue] = useState<
		Pick<Header.Value, "search" | "searchType">
	>({
		search: "",
		searchType: "address",
	});
	const [showValue, setShowValue] =
		useState<Orders.Map.BtnOnMapToggles>(btnOnMap);

	const headerValue = useMemo<Header.Value>(
		() => ({
			...searchValue,
			...mapWidgetExecutorFilters,
		}),
		[mapWidgetExecutorFilters, searchValue],
	);

	const setHeaderValue = useCallback(
		({ search, searchType, ...executorFilters }: Header.Value) => {
			const companyIds =
				executorFilters.companyIds?.[0] === "all"
					? accessCompaniesIds
					: executorFilters.companyIds;
			const taxiServiceIds =
				executorFilters.taxiServiceIds?.[0] === "all"
					? accessTaxiServicesIds
					: executorFilters.taxiServiceIds;
			const _executorFilters = {
				...executorFilters,
				companyIds,
				taxiServiceIds,
			};
			setSearchValue({
				search,
				searchType,
			});
			setMapWidgetOrderTabExecutorFilters(
				activeOrderTab,
				_executorFilters,
			);
		},
		[
			accessCompaniesIds,
			accessTaxiServicesIds,
			activeOrderTab,
			setMapWidgetOrderTabExecutorFilters,
		],
	);

	const headerValueEditor = useObjectEditor(headerValue, setHeaderValue);

	const headerValueSearch = headerValueEditor.useGetter("search");
	const setHeaderValueSearch = headerValueEditor.useSetter("search");

	useChanged(
		() => {
			if (!mapRef.current || "id" in activeOrder) return;

			mapRef.current.setView(mapCenter ?? defaultMapCenter);
		},
		mapCenter,
		true,
	);

	const mapSearchResult = useMapSearch({
		query: headerValue.searchType === "address" ? headerValue.search : "",
		country: "ua",
		searchType: [],
		language,
		near: {
			point: mapRef.current?.getCenter() ?? mapCenter ?? defaultMapCenter,
			zoom: mapRef.current?.getZoom() ?? undefined,
		},
	});

	const executorsFilters = useMemo<ExecutorLocations.SubscribeOptions>(() => {
		if (headerValue.onActiveOrder) {
			return {
				orderIds: "id" in activeOrder ? [activeOrder.id] : [],
			};
		}

		const open = headerValue.shifts.includes("open");
		const closed = headerValue.shifts.includes("closed");

		return {
			taxiServiceIds: decodeTaxiServiceIds(
				headerValue.companyIds,
				headerValue.taxiServiceIds,
			),

			executorWorkingStatus:
				(open && closed) || (!open && !closed) ? undefined : open,

			executorStatuses: headerValue.statuses
				.map((status) => headerValueStatusToExecutorStatus[status])
				.filter(Boolean) as ExecutorStatus[],
			fleetIds: headerValue.fleetIds,
		};
	}, [
		activeOrder,
		decodeTaxiServiceIds,
		headerValue.companyIds,
		headerValue.fleetIds,
		headerValue.onActiveOrder,
		headerValue.shifts,
		headerValue.statuses,
		headerValue.taxiServiceIds,
	]);

	const getMapLocations = useCallback(
		() =>
			selectFilteredExecutorLocations(
				store.getState(),
				executorsFilters as ExecutorLocationSubscribeOptions,
				locationAccess,
			),
		[executorsFilters, locationAccess],
	);

	const searchOptions = useMemo<
		Header.SearchOption<
			MapService.Search.Object | ExecutorLocations.Model
		>[]
	>(() => {
		if (headerValue.searchType === "address") {
			return mapSearchResult.loading
				? []
				: mapSearchResult.objects.map((object) => {
						const address = {
							country: object.country ?? "",
							countryCode: object.countryCode ?? "",
							street: object.street ?? "",
							streetType: object.streetType ?? "",
							settlement: object.settlement ?? "",
							settlementType: object.settlementType ?? "",
							district: object.district ?? "",
							region: object.region ?? "",
							house: object.number ?? "",
						};
						return {
							key: object.id,
							label: [addressToString(address), object.name]
								.filter(Boolean)
								.join(", "),
							value: object,
						};
				  });
		}

		if (headerValue.search === "") {
			return [];
		}

		const query = headerValue.search.toLowerCase();
		return getMapLocations()
			.filter((executorLocation) =>
				executorLocation.executor.alias.toLowerCase().includes(query),
			)
			.map((executorLocation) => ({
				key: executorLocation.id,
				label: executorLocation.executor.alias,
				value: executorLocation,
			}));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		getMapLocations,
		headerValue.search,
		headerValue.searchType,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		!mapSearchResult.loading && mapSearchResult.objects,
	]);

	const headerOnChange = useCallback(
		(value: Header.Value) => {
			if (value.searchType !== headerValue.searchType) {
				// eslint-disable-next-line no-param-reassign
				value = assign(clone(value), { search: "" });
			}

			setHeaderValue(value);

			if (value.search !== headerValueSearch) {
				setObject(null);
				executorsRef.current?.unfocus();
			}
		},
		[headerValue.searchType, headerValueSearch, setHeaderValue],
	);

	const mapOnReady = useCallback(() => {
		routeRef.current?.focus();
	}, []);

	const controlButtonsOnCenter = useCallback(() => {
		const executors = executorsRef.current;

		if (executors && executors.focus()) return;

		const mapObject = mapObjectRef.current;

		if (mapObject && mapObject.focus()) return;

		const route = routeRef.current;

		if (!route) return;

		route.focus();
	}, []);

	const controlButtonsOnZoomIn = useCallback(() => {
		if (!mapRef.current) {
			return;
		}

		mapRef.current.zoomIn();
	}, []);

	const controlButtonsOnZoomOut = useCallback(() => {
		if (!mapRef.current) {
			return;
		}

		mapRef.current.zoomOut();
	}, []);

	useLayoutEffect(() => {
		const route = routeRef.current;

		if (!route) return;

		route.focusPoint(activePoint);
	}, [activePoint]);

	const headerOnSelectSearchOptions = useCallback(
		async (
			option: Header.SearchOption<
				MapService.Search.Object | ExecutorLocations.Model
			>,
		) => {
			if (isMapObject(option.value)) {
				if (
					option?.value?.searchType === "google_geocode" ||
					option?.value?.searchType === "osm_nominatim"
				) {
					const searchOptions: MapService.Search.Options = {
						language,
						searchType: "address",
						country: "ua",
						near: {
							point:
								mapRef.current?.getCenter() ??
								mapCenter ??
								defaultMapCenter,
							radius: searchRadius,
						},
					};

					const result = await MapService.search(
						option.label,
						searchOptions,
					);

					if (result.objects?.length) {
						const selectedObject = result.objects[0];
						if (selectedObject.coordinates) {
							dispatch(
								orderPage.actions.setMapCenter(
									selectedObject.coordinates,
								),
							);
						}
						setObject(selectedObject);
					} else {
						setObject(option.value);
					}
				} else {
					setObject(option.value);
				}
			} else {
				executorsRef.current?.focusExecutor(option.value.executor.id);
			}

			setHeaderValueSearch(option.label);
		},
		[
			language,
			searchRadius,
			mapCenter,
			defaultMapCenter,
			dispatch,
			setHeaderValueSearch,
		],
	);

	useEffect(() => {
		if (
			"id" in activeOrder ||
			!currentTaxiService?.coordinates ||
			mapCenter
		)
			return;

		setMapWidgetCenter(currentTaxiService?.coordinates);
	}, [
		activeOrder,
		currentTaxiService?.coordinates,
		currentTaxiService?.coordinates.lat,
		currentTaxiService?.coordinates.lng,
		setMapWidgetCenter,
		mapCenter,
	]);

	const taxiServiceIds = useMemo(() => {
		const ids: number[] = [];

		headerValue?.taxiServiceIds?.forEach((item) => {
			if (isNumber(item)) ids.push(item);
		});

		if (ids.length) return ids;
		return undefined;
	}, [headerValue]);

	return (
		<Root sizes="auto! 1fr" maxedWidth maxedHeight>
			<Header
				value={headerValue}
				searchOptions={searchOptions}
				defaultFiltersValue={mapWidgetDefaultExecutorsFilters}
				onChange={headerOnChange}
				onSelectSearchOption={headerOnSelectSearchOptions}
			/>
			<MapBase
				ref={mapRef}
				center={mapCenter ?? defaultMapCenter}
				zoomControl={false}
				zoom={11}
				maxZoom={19}
				onReady={mapOnReady}
				onChangeCenter={setMapWidgetCenter}
				onChangeZoom={setMapWidgetZoom}
				hasRasterization={taxiServiceName === "visicom"}
				nameMap={taxiServiceName}
				language={language}
			>
				<Control prepend position="topright">
					<ShowButtons value={showValue} onChange={setShowValue} />
				</Control>
				<Control prepend position="bottomright">
					<ControlButtons
						onCenter={controlButtonsOnCenter}
						onZoomIn={controlButtonsOnZoomIn}
						onZoomOut={controlButtonsOnZoomOut}
					/>
				</Control>

				<MapObject ref={mapObjectRef} object={object} />
				<AccessGuard
					accessName={TabAccessNames[TabKeys.MAP_FILTER_POINTS]}
					canShow={showValue.route && !object}
				>
					<Route ref={routeRef} />
				</AccessGuard>
				<AccessGuard
					accessName={TabAccessNames[TabKeys.MAP_FILTER_PRICE_ZONE]}
					canShow={showValue.priceZones}
				>
					<PriceZones taxiServiceIds={taxiServiceIds} />
				</AccessGuard>
				<AccessGuard
					accessName={TabAccessNames[TabKeys.MAP_FILTER_SECTOR]}
					canShow={showValue.sectors}
				>
					<Sectors taxiServiceIds={taxiServiceIds} />
				</AccessGuard>
				<AccessGuard
					accessName={TabAccessNames[TabKeys.MAP_FILTER_PARKING]}
					canShow={showValue.parkings}
				>
					<Parkings taxiServiceIds={taxiServiceIds} />
				</AccessGuard>
				<AccessGuard
					accessName={TabAccessNames[TabKeys.MAP_FILTER_EXECUTOR]}
					canShow={showValue.executors}
				>
					<MapExecutorLocations
						ref={executorsRef}
						getLocations={getMapLocations}
						highlightedExecutorId={highlightedExecutorId}
					/>
				</AccessGuard>
			</MapBase>
		</Root>
	);
};

declare namespace Map {
	interface Props {}
}

export default memo(Map);
